/**
 * Block Kit (Slack's layout blocks, docs/features/integrations.md): header, section (text, fields, an accessory),
 * divider, context, image, actions, input (label, hint, optional, one element) and rich_text (read-only), plus the
 * interactive elements — button (with an optional `confirm` dialog), plain_text_input, static (options or
 * `option_groups`) / external (options loaded from the app) / users / conversations / channels selects and their
 * multi_ versions, datepicker, timepicker, checkboxes, radio_buttons and overflow. Field names are Slack's
 * (snake_case), so a payload built for Slack works here unchanged.
 *
 * Apps send blocks in messages (incoming webhooks, slash-command / interactivity answers, response_url, the Web API),
 * in modals (`KitView`, views.ts) and in their Home tab (`KitHomeView`, views.publish). Shape is checked here; the cross-field rules (unique ids, a section needs text
 * or fields, https URLs, initial options among the options, where each element may go) are core `logic/blockKit`
 * `kitProblems` / `viewProblems`.
 *
 * `KitStateValues` is Slack's `state.values` (block_id → action_id → value), sent with block_actions and
 * view_submission payloads.
 */
import { z } from "zod";

/** Most blocks in one message (Slack: 50). */
export const KIT_MAX_BLOCKS = 50;
/** Most blocks in one modal (Slack: 100). */
export const KIT_MAX_VIEW_BLOCKS = 100;

export const KitPlainText = z.object({ type: z.literal("plain_text"), text: z.string().min(1).max(3000), emoji: z.boolean().optional() });
export type KitPlainText = z.infer<typeof KitPlainText>;

/** Slack mrkdwn: *bold*, _italic_, ~strike~, `code`, <url|label>, <@user>, <#channel>. */
export const KitMrkdwn = z.object({ type: z.literal("mrkdwn"), text: z.string().min(1).max(3000), verbatim: z.boolean().optional() });
export type KitMrkdwn = z.infer<typeof KitMrkdwn>;

export const KitText = z.discriminatedUnion("type", [KitPlainText, KitMrkdwn]);
export type KitText = z.infer<typeof KitText>;

/** "Are you sure?" before a button press or menu choice goes to the app (Slack's confirmation dialog object). */
export const KitConfirm = z.object({
  title: KitPlainText.extend({ text: z.string().min(1).max(100) }),
  text: KitText,
  confirm: KitPlainText.extend({ text: z.string().min(1).max(30) }),
  deny: KitPlainText.extend({ text: z.string().min(1).max(30) }),
  style: z.enum(["primary", "danger"]).optional(),
});
export type KitConfirm = z.infer<typeof KitConfirm>;

export const KitButton = z.object({
  type: z.literal("button"),
  text: KitPlainText.extend({ text: z.string().min(1).max(75) }),
  action_id: z.string().min(1).max(255),
  value: z.string().max(2000).optional(),
  /** Opens this link instead of (as well as, for Slack) calling the app. */
  url: z.string().max(3000).optional(),
  style: z.enum(["primary", "danger"]).optional(),
  confirm: KitConfirm.optional(),
  accessibility_label: z.string().max(75).optional(),
});
export type KitButton = z.infer<typeof KitButton>;

export const KitImageElement = z.object({ type: z.literal("image"), image_url: z.string().min(1).max(3000), alt_text: z.string().max(2000) });
export type KitImageElement = z.infer<typeof KitImageElement>;

/** One choice of a select, overflow menu, checkbox group or radio group (mrkdwn only in checkboxes and radios). */
export const KitOption = z.object({
  text: KitText,
  value: z.string().min(1).max(150),
  description: KitText.optional(),
});
export type KitOption = z.infer<typeof KitOption>;

/** A labelled group of a select's options (Slack's option group): up to 100 groups of up to 100 options. */
export const KitOptionGroup = z.object({
  label: KitPlainText.extend({ text: z.string().min(1).max(75) }),
  options: z.array(KitOption).min(1).max(100),
});
export type KitOptionGroup = z.infer<typeof KitOptionGroup>;

const actionId = z.string().min(1).max(255);
const placeholder = KitPlainText.extend({ text: z.string().min(1).max(150) });
const options = (max: number) => z.array(KitOption).min(1).max(max);
const ymd = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
const hhmm = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/);

/** Which conversations a conversations_select lists (Slack's filter). */
export const KitConversationFilter = z.object({
  include: z.array(z.enum(["im", "mpim", "private", "public"])).min(1).max(4).optional(),
  exclude_bot_users: z.boolean().optional(),
  exclude_external_shared_channels: z.boolean().optional(),
});
export type KitConversationFilter = z.infer<typeof KitConversationFilter>;

/** A one-line or multiline text box (input blocks only). */
export const KitPlainTextInput = z.object({
  type: z.literal("plain_text_input"),
  action_id: actionId,
  placeholder: placeholder.optional(),
  initial_value: z.string().max(3000).optional(),
  multiline: z.boolean().optional(),
  min_length: z.number().int().min(0).max(3000).optional(),
  max_length: z.number().int().min(1).max(3000).optional(),
  focus_on_load: z.boolean().optional(),
});
export type KitPlainTextInput = z.infer<typeof KitPlainTextInput>;

/** Options or option_groups (exactly one: core `kitProblems`). */
export const KitStaticSelect = z.object({
  type: z.literal("static_select"),
  action_id: actionId,
  placeholder: placeholder.optional(),
  options: options(100).optional(),
  option_groups: z.array(KitOptionGroup).min(1).max(100).optional(),
  initial_option: KitOption.optional(),
  confirm: KitConfirm.optional(),
  focus_on_load: z.boolean().optional(),
});
export type KitStaticSelect = z.infer<typeof KitStaticSelect>;

export const KitMultiStaticSelect = z.object({
  type: z.literal("multi_static_select"),
  action_id: actionId,
  placeholder: placeholder.optional(),
  options: options(100).optional(),
  option_groups: z.array(KitOptionGroup).min(1).max(100).optional(),
  initial_options: z.array(KitOption).max(100).optional(),
  max_selected_items: z.number().int().min(1).optional(),
  confirm: KitConfirm.optional(),
  focus_on_load: z.boolean().optional(),
});
export type KitMultiStaticSelect = z.infer<typeof KitMultiStaticSelect>;

/**
 * Options the app supplies as the person types (Slack's external_select): each query is a signed `block_suggestion`
 * to the app's options load URL (apps.ts `App.optionsUrl`), after `min_query_length` characters (default 3).
 */
export const KitExternalSelect = z.object({
  type: z.literal("external_select"),
  action_id: actionId,
  placeholder: placeholder.optional(),
  initial_option: KitOption.optional(),
  min_query_length: z.number().int().min(0).max(100).optional(),
  confirm: KitConfirm.optional(),
  focus_on_load: z.boolean().optional(),
});
export type KitExternalSelect = z.infer<typeof KitExternalSelect>;

export const KitMultiExternalSelect = z.object({
  type: z.literal("multi_external_select"),
  action_id: actionId,
  placeholder: placeholder.optional(),
  initial_options: z.array(KitOption).max(100).optional(),
  min_query_length: z.number().int().min(0).max(100).optional(),
  max_selected_items: z.number().int().min(1).optional(),
  confirm: KitConfirm.optional(),
  focus_on_load: z.boolean().optional(),
});
export type KitMultiExternalSelect = z.infer<typeof KitMultiExternalSelect>;

export const KitUsersSelect = z.object({
  type: z.literal("users_select"),
  action_id: actionId,
  placeholder: placeholder.optional(),
  initial_user: z.string().min(1).max(64).optional(),
  confirm: KitConfirm.optional(),
  focus_on_load: z.boolean().optional(),
});
export type KitUsersSelect = z.infer<typeof KitUsersSelect>;

export const KitMultiUsersSelect = z.object({
  type: z.literal("multi_users_select"),
  action_id: actionId,
  placeholder: placeholder.optional(),
  initial_users: z.array(z.string().min(1).max(64)).max(100).optional(),
  max_selected_items: z.number().int().min(1).optional(),
  confirm: KitConfirm.optional(),
  focus_on_load: z.boolean().optional(),
});
export type KitMultiUsersSelect = z.infer<typeof KitMultiUsersSelect>;

export const KitConversationsSelect = z.object({
  type: z.literal("conversations_select"),
  action_id: actionId,
  placeholder: placeholder.optional(),
  initial_conversation: z.string().min(1).max(64).optional(),
  /** Starts on the conversation the modal / message was opened from. */
  default_to_current_conversation: z.boolean().optional(),
  filter: KitConversationFilter.optional(),
  confirm: KitConfirm.optional(),
  focus_on_load: z.boolean().optional(),
});
export type KitConversationsSelect = z.infer<typeof KitConversationsSelect>;

export const KitMultiConversationsSelect = z.object({
  type: z.literal("multi_conversations_select"),
  action_id: actionId,
  placeholder: placeholder.optional(),
  initial_conversations: z.array(z.string().min(1).max(64)).max(100).optional(),
  default_to_current_conversation: z.boolean().optional(),
  filter: KitConversationFilter.optional(),
  max_selected_items: z.number().int().min(1).optional(),
  confirm: KitConfirm.optional(),
  focus_on_load: z.boolean().optional(),
});
export type KitMultiConversationsSelect = z.infer<typeof KitMultiConversationsSelect>;

/** Public channels only (Slack's channels_select). */
export const KitChannelsSelect = z.object({
  type: z.literal("channels_select"),
  action_id: actionId,
  placeholder: placeholder.optional(),
  initial_channel: z.string().min(1).max(64).optional(),
  confirm: KitConfirm.optional(),
  focus_on_load: z.boolean().optional(),
});
export type KitChannelsSelect = z.infer<typeof KitChannelsSelect>;

export const KitMultiChannelsSelect = z.object({
  type: z.literal("multi_channels_select"),
  action_id: actionId,
  placeholder: placeholder.optional(),
  initial_channels: z.array(z.string().min(1).max(64)).max(100).optional(),
  max_selected_items: z.number().int().min(1).optional(),
  confirm: KitConfirm.optional(),
  focus_on_load: z.boolean().optional(),
});
export type KitMultiChannelsSelect = z.infer<typeof KitMultiChannelsSelect>;

/** A day, "YYYY-MM-DD". */
export const KitDatepicker = z.object({
  type: z.literal("datepicker"),
  action_id: actionId,
  placeholder: placeholder.optional(),
  initial_date: ymd.optional(),
  confirm: KitConfirm.optional(),
  focus_on_load: z.boolean().optional(),
});
export type KitDatepicker = z.infer<typeof KitDatepicker>;

/** A time of day, "HH:mm" (24 h). */
export const KitTimepicker = z.object({
  type: z.literal("timepicker"),
  action_id: actionId,
  placeholder: placeholder.optional(),
  initial_time: hhmm.optional(),
  timezone: z.string().max(100).optional(),
  confirm: KitConfirm.optional(),
  focus_on_load: z.boolean().optional(),
});
export type KitTimepicker = z.infer<typeof KitTimepicker>;

export const KitCheckboxes = z.object({
  type: z.literal("checkboxes"),
  action_id: actionId,
  options: options(10),
  initial_options: z.array(KitOption).max(10).optional(),
  confirm: KitConfirm.optional(),
  focus_on_load: z.boolean().optional(),
});
export type KitCheckboxes = z.infer<typeof KitCheckboxes>;

export const KitRadioButtons = z.object({
  type: z.literal("radio_buttons"),
  action_id: actionId,
  options: options(10),
  initial_option: KitOption.optional(),
  confirm: KitConfirm.optional(),
  focus_on_load: z.boolean().optional(),
});
export type KitRadioButtons = z.infer<typeof KitRadioButtons>;

/** The "⋯" menu: up to 5 choices, each sent to the app when picked. */
export const KitOverflow = z.object({
  type: z.literal("overflow"),
  action_id: actionId,
  options: options(5),
  confirm: KitConfirm.optional(),
});
export type KitOverflow = z.infer<typeof KitOverflow>;

/**
 * Every block element. Where each may go is Slack's rule, checked by core `kitProblems`: a section accessory is an
 * image or anything interactive but a text input; an actions block takes anything interactive but a text input; an
 * input block takes one element that holds a value (not a button, overflow menu or image).
 */
export const KitElement = z.discriminatedUnion("type", [
  KitButton,
  KitImageElement,
  KitPlainTextInput,
  KitStaticSelect,
  KitMultiStaticSelect,
  KitExternalSelect,
  KitMultiExternalSelect,
  KitUsersSelect,
  KitMultiUsersSelect,
  KitConversationsSelect,
  KitMultiConversationsSelect,
  KitChannelsSelect,
  KitMultiChannelsSelect,
  KitDatepicker,
  KitTimepicker,
  KitCheckboxes,
  KitRadioButtons,
  KitOverflow,
]);
export type KitElement = z.infer<typeof KitElement>;

/** A context element: small text or a tiny image. */
export const KitContextElement = z.discriminatedUnion("type", [KitPlainText.extend({}), KitMrkdwn.extend({}), KitImageElement.extend({})]);
export type KitContextElement = z.infer<typeof KitContextElement>;

// ---------- rich_text (read-only) ----------

export const KitRichStyle = z.object({ bold: z.boolean().optional(), italic: z.boolean().optional(), strike: z.boolean().optional(), code: z.boolean().optional() });
export type KitRichStyle = z.infer<typeof KitRichStyle>;

/** Inline pieces of a rich_text element (Slack's names). */
export const KitRichInline = z.discriminatedUnion("type", [
  z.object({ type: z.literal("text"), text: z.string().max(4000), style: KitRichStyle.optional() }),
  z.object({ type: z.literal("link"), url: z.string().max(3000), text: z.string().max(4000).optional(), style: KitRichStyle.optional() }),
  z.object({ type: z.literal("user"), user_id: z.string().min(1).max(64), style: KitRichStyle.optional() }),
  z.object({ type: z.literal("channel"), channel_id: z.string().min(1).max(64), style: KitRichStyle.optional() }),
  z.object({ type: z.literal("usergroup"), usergroup_id: z.string().min(1).max(64), style: KitRichStyle.optional() }),
  /** `unicode`: the code points in hex, dash-separated ("1f44d"). */
  z.object({ type: z.literal("emoji"), name: z.string().min(1).max(100), unicode: z.string().max(100).optional() }),
  z.object({ type: z.literal("broadcast"), range: z.enum(["here", "channel", "everyone"]) }),
]);
export type KitRichInline = z.infer<typeof KitRichInline>;

const richSection = z.object({ type: z.literal("rich_text_section"), elements: z.array(KitRichInline).max(200) });

export const KitRichElement = z.discriminatedUnion("type", [
  richSection,
  z.object({
    type: z.literal("rich_text_list"),
    style: z.enum(["bullet", "ordered"]),
    elements: z.array(richSection.extend({})).max(100),
    indent: z.number().int().min(0).max(8).optional(),
    offset: z.number().int().min(0).optional(),
    border: z.number().int().min(0).max(1).optional(),
  }),
  z.object({ type: z.literal("rich_text_preformatted"), elements: z.array(KitRichInline).max(200), border: z.number().int().min(0).max(1).optional() }),
  z.object({ type: z.literal("rich_text_quote"), elements: z.array(KitRichInline).max(200), border: z.number().int().min(0).max(1).optional() }),
]);
export type KitRichElement = z.infer<typeof KitRichElement>;

// ---------- blocks ----------

const blockId = z.string().min(1).max(255).optional();

export const KitBlock = z.discriminatedUnion("type", [
  z.object({ type: z.literal("header"), block_id: blockId, text: KitPlainText.extend({ text: z.string().min(1).max(150) }) }),
  z.object({
    type: z.literal("section"),
    block_id: blockId,
    text: KitText.optional(),
    fields: z.array(KitText).max(10).optional(),
    accessory: KitElement.optional(),
  }),
  z.object({ type: z.literal("divider"), block_id: blockId }),
  z.object({ type: z.literal("context"), block_id: blockId, elements: z.array(KitContextElement).min(1).max(10) }),
  z.object({ type: z.literal("image"), block_id: blockId, image_url: z.string().min(1).max(3000), alt_text: z.string().max(2000), title: KitPlainText.extend({}).optional() }),
  z.object({ type: z.literal("actions"), block_id: blockId, elements: z.array(KitElement).min(1).max(25) }),
  /** A labelled field of a modal (Slack's input block): one element, a hint under it, "(optional)" when not required. */
  z.object({
    type: z.literal("input"),
    block_id: blockId,
    label: KitPlainText.extend({ text: z.string().min(1).max(2000) }),
    element: KitElement,
    hint: KitPlainText.extend({ text: z.string().min(1).max(2000) }).optional(),
    optional: z.boolean().optional(),
    /** Changes send block_actions at once (Slack's dispatch_action). */
    dispatch_action: z.boolean().optional(),
  }),
  /** Formatted text (Slack's rich_text block): paragraphs, lists, code blocks and quotes; read-only. */
  z.object({ type: z.literal("rich_text"), block_id: blockId, elements: z.array(KitRichElement).min(1).max(100) }),
]);
export type KitBlock = z.infer<typeof KitBlock>;

export const KitBlocks = z.array(KitBlock).max(KIT_MAX_BLOCKS);
export type KitBlocks = z.infer<typeof KitBlocks>;

// ---------- state ----------

/**
 * One element's value in `state.values` (Slack's shape): `type` is the element type and the key that's set depends on
 * it — `value` (plain_text_input), `selected_option` (static_select, external_select, radio_buttons, overflow),
 * `selected_options` (multi_static_select, multi_external_select, checkboxes), `selected_user(s)`, `selected_conversation(s)`, `selected_channel(s)`,
 * `selected_date`, `selected_time`. Nothing chosen = the key is absent.
 */
export const KitStateValue = z.object({
  type: z.string().min(1).max(64),
  value: z.string().max(3000).optional(),
  selected_option: KitOption.optional(),
  selected_options: z.array(KitOption).max(100).optional(),
  selected_user: z.string().max(64).optional(),
  selected_users: z.array(z.string().max(64)).max(100).optional(),
  selected_conversation: z.string().max(64).optional(),
  selected_conversations: z.array(z.string().max(64)).max(100).optional(),
  selected_channel: z.string().max(64).optional(),
  selected_channels: z.array(z.string().max(64)).max(100).optional(),
  selected_date: z.string().max(10).optional(),
  selected_time: z.string().max(5).optional(),
});
export type KitStateValue = z.infer<typeof KitStateValue>;

/** `state.values`: block_id → action_id → value. */
export const KitStateValues = z.record(z.string().max(255), z.record(z.string().max(255), KitStateValue));
export type KitStateValues = z.infer<typeof KitStateValues>;

// ---------- modals ----------

const viewText = KitPlainText.extend({ text: z.string().min(1).max(24) });

/** A modal an app opens (Slack's view object, type "modal"); views.ts has the stack and the calls. */
export const KitView = z.object({
  type: z.literal("modal"),
  title: viewText,
  blocks: z.array(KitBlock).max(KIT_MAX_VIEW_BLOCKS),
  /** The submit button; required when the view has input blocks. */
  submit: viewText.optional(),
  /** The close button ("Cancel" when absent). */
  close: viewText.optional(),
  /** Up to 3000 characters the app gets back with every payload about this view. */
  private_metadata: z.string().max(3000).optional(),
  callback_id: z.string().max(255).optional(),
  /** Closing this view closes the whole stack. */
  clear_on_close: z.boolean().optional(),
  /** The app gets a view_closed payload when the person closes it. */
  notify_on_close: z.boolean().optional(),
  /** The app's own id for the view (views.update by external_id), unique per workspace. */
  external_id: z.string().max(255).optional(),
  submit_disabled: z.boolean().optional(),
});
export type KitView = z.infer<typeof KitView>;

/**
 * An app's Home tab (Slack's view of type "home", published per person with `views.publish`): blocks only, no title
 * or buttons of its own. Shown in the app's DM, above Messages.
 */
export const KitHomeView = z.object({
  type: z.literal("home"),
  blocks: z.array(KitBlock).max(KIT_MAX_VIEW_BLOCKS),
  private_metadata: z.string().max(3000).optional(),
  callback_id: z.string().max(255).optional(),
  external_id: z.string().max(255).optional(),
});
export type KitHomeView = z.infer<typeof KitHomeView>;
